import { useCallback, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { formatImportProductErrors, translateErrorMessage } from '@/utils/errorTranslations';
import { ApiImportError } from '@/utils/httpUtils';

interface UseAddProductProps {
  onClose?: () => void;
  onProductsAdded?: () => Promise<void>;
}

const useAddProduct = ({ onClose, onProductsAdded }: UseAddProductProps = {}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [urlTemplate, setUrlTemplate] = useState<string | null>(null);
  const [isPendingTemplate, startTransition] = useTransition();
  const [images, setImages] = useState<File[]>([]);
  const [isPendingImport, startTransitionImport] = useTransition();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
  });

  const handleChangeUrlTemplate = useCallback((url: string | null) => {
    setUrlTemplate(url);
  }, []);

  const handleChangeStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const getTemplate = async (images: File[]) => {
    const http = initializeHttpProvider();
    const formData = new FormData();
    images.forEach((f) => {
      formData.append('images', f);
    });
    startTransition(async () => {
      try {
        const response = await http.inventory.templateProducts(formData);
        if (response.success) {
          setUrlTemplate(response.csvURL);
          toast.success('Plantilla generada correctamente');
          return;
        }
        toast.error('Error al generar la plantilla');
      } catch {
        toast.error('Error al generar la plantilla');
      }
    });
  };

  const importProducts = async (file: File) => {
    const http = initializeHttpProvider();
    const formData = new FormData();
    formData.append('file', file);
    startTransitionImport(async () => {
      try {
        const response = await http.inventory.importProducts(formData);

        if (response.products?.length > 0) {
          if (response.errors?.length > 0) {
            const errorDetail = formatImportProductErrors(response.errors);
            toast.warning(
              `${response.products.length} producto(s) importado(s) correctamente, pero hubo errores:\n${errorDetail}`,
            );
          } else {
            toast.success('Productos importados correctamente');
          }

          if (onClose) {
            onClose();
          }
          if (onProductsAdded) {
            await onProductsAdded();
          }
          return;
        }

        if (response.errors?.length > 0) {
          const errorDetail = formatImportProductErrors(response.errors);
          toast.error(`Error al importar productos:\n${errorDetail}`);
          return;
        }

        toast.error('No se importaron productos.');
      } catch (e: unknown) {
        if (e instanceof ApiImportError) {
          const errorDetail = formatImportProductErrors(e.errors);
          toast.error(`Error al importar productos:\n${errorDetail}`);
          return;
        }

        if (e instanceof Error && e.message) {
          const detail = translateErrorMessage(e.message);
          toast.error(`Error al importar productos:\n• ${detail}`);

          return;
        }

        toast.error('Error al importar productos. Por favor, intenta nuevamente.');
      }
    });
  };

  return {
    currentStep,
    urlTemplate,
    register,
    handleSubmit,
    errors,
    handleChangeUrlTemplate,
    handleChangeStep,
    importProducts,
    getTemplate,
    isPendingTemplate,
    isPendingImport,
    images,
    setImages,
  };
};

export default useAddProduct;
